import {useState, useEffect, useRef, useCallback} from 'react';
import {createMutationErrorHandler, RouterOutput, trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {useToast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {UserPlus, Loader2, Search} from 'lucide-react';
import {SYSTEM_ROLES} from '@common/roles';
import {CreateRegistrationResult, INVITE_EXPIRATION_DAY_OPTIONS} from '@internal/pages/users/new';
import {FeatureFlagEditor, getDefaultFeatureFlags} from './featureFlagConfig';
import {AssignableRolePicker} from './AssignableRolePicker';

type Group = RouterOutput['internal']['userManagement']['getGroups']['groups'][number];
type GroupPreset = RouterOutput['internal']['userManagement']['getGroupPresets'][number];

export function GroupUserForm({onSuccess}: {onSuccess: (data: CreateRegistrationResult) => void}) {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const {toast} = useToast();
  const [loadingPreset, setLoadingPreset] = useState(false);
  const [groupForm, setGroupForm] = useState({
    email: '',
    name: '',
    clientId: '',
    selectedGroups: [] as {vbaGroupId: string}[],
    groupSearch: '',
    role: SYSTEM_ROLES.group.admin.key as string,
    expiryDays: 7,
    featureFlags: getDefaultFeatureFlags(),
  });

  const {data: clients, isLoading: clientsIsLoading} =
    trpc.internal.userManagement.getClients.useQuery();

  // For client users, auto-select their client
  useEffect(() => {
    if (!isInternalUser && clients && clients.length === 1 && !groupForm.clientId) {
      setGroupForm((prev) => ({...prev, clientId: clients[0].id}));
    }
  }, [clients, isInternalUser, groupForm.clientId]);

  const {
    data: groupsData,
    isLoading: groupsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.internal.userManagement.getGroups.useInfiniteQuery(
    {
      clientId: groupForm.clientId,
      search: groupForm.groupSearch,
    },
    {
      enabled: !!groupForm.clientId,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  const groups = groupsData?.pages.flatMap((page) => page.groups) ?? [];

  const scrollObserverRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollObserverRef.current) scrollObserverRef.current.disconnect();
      if (!node) return;
      scrollObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      scrollObserverRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const {data: groupPresets} = trpc.internal.userManagement.getGroupPresets.useQuery(undefined, {
    enabled: isInternalUser,
  });

  const getPresetGroups = trpc.internal.userManagement.getPresetGroups.useMutation();

  const createGroupUser = trpc.internal.userManagement.createGroupUser.useMutation({
    onSuccess: (data) => {
      onSuccess(data.registration);
      toast({
        title: 'Registration created',
        description: `Access code has been generated for ${groupForm.email}`,
      });
    },
    onError: createMutationErrorHandler('Error creating registration'),
  });

  const handleGroupSubmit = () => {
    createGroupUser.mutate({
      email: groupForm.email,
      name: groupForm.name,
      clientId: groupForm.clientId,
      groups: groupForm.selectedGroups,
      role: groupForm.role,
      expiryDays: groupForm.expiryDays,
      featureFlags: groupForm.featureFlags,
    });
  };

  const handleGroupToggle = (group: Group) => {
    const isSelected = groupForm.selectedGroups.some((g) => g.vbaGroupId === group.vbaGroupId);
    if (isSelected) {
      setGroupForm({
        ...groupForm,
        selectedGroups: groupForm.selectedGroups.filter((g) => g.vbaGroupId !== group.vbaGroupId),
      });
    } else {
      setGroupForm({
        ...groupForm,
        selectedGroups: [...groupForm.selectedGroups, {vbaGroupId: group.vbaGroupId}],
      });
    }
  };

  const handlePresetSelect = async (preset: GroupPreset) => {
    setLoadingPreset(true);
    try {
      const result = await getPresetGroups.mutateAsync({
        preset: preset.key,
      });

      if (result) {
        const presetGroups = result.map((g) => ({vbaGroupId: g.vbaGroupId}));

        setGroupForm({
          ...groupForm,
          clientId: preset.clientId,
          selectedGroups: presetGroups,
        });

        toast({
          title: 'Preset Applied',
          description: `Selected ${presetGroups.length} groups from ${preset.name} preset`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error loading preset',
        description: 'Failed to load preset groups',
        variant: 'destructive',
      });
    } finally {
      setLoadingPreset(false);
    }
  };

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>
          Group User Registration
        </CardTitle>
      </CardHeader>
      <CardContent className='p-6 space-y-6'>
        <div className='grid grid-cols-2 gap-6'>
          <div className='space-y-2'>
            <Label htmlFor='group-email'>Email Address</Label>
            <Input
              id='group-email'
              type='email'
              placeholder='user@example.com'
              value={groupForm.email}
              onChange={(e) => setGroupForm({...groupForm, email: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='group-name'>Full Name</Label>
            <Input
              id='group-name'
              placeholder='John Doe'
              value={groupForm.name}
              onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
        </div>
        {isInternalUser ? (
          <div className='space-y-2'>
            <Label htmlFor='group-affiliation'>Client Affiliation</Label>
            {clientsIsLoading ? (
              <Skeleton className='h-10 w-full' />
            ) : (
              <Select
                value={groupForm.clientId}
                onValueChange={(value) =>
                  setGroupForm({...groupForm, clientId: value, selectedGroups: []})
                }
              >
                <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                  <SelectValue placeholder='Select affiliation' />
                </SelectTrigger>
                <SelectContent>
                  {clients?.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          clients &&
          clients.length > 0 && (
            <div className='space-y-2'>
              <Label>Client</Label>
              <div className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-brand-navy font-medium'>
                {clients[0].name}
              </div>
            </div>
          )
        )}
        {groupForm.clientId && (
          <>
            {isInternalUser &&
              groupPresets &&
              groupPresets
                .filter((p) => p.clientId === groupForm.clientId)
                .map((preset) => (
                  <div key={preset.key} className='space-y-2'>
                    <Label>Quick Presets</Label>
                    <div className='flex gap-2'>
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => handlePresetSelect(preset)}
                        disabled={loadingPreset}
                        className='border-brand-blue text-brand-blue hover:bg-brand-blue/10'
                      >
                        {loadingPreset ? (
                          <>
                            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                            Loading...
                          </>
                        ) : (
                          `Select ${preset.name} Groups (${preset.groupCount})`
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
            <div className='space-y-2'>
              <Label>Select Groups</Label>
              <div className='relative mb-2'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                <Input
                  placeholder='Search groups...'
                  value={groupForm.groupSearch}
                  onChange={(e) => setGroupForm({...groupForm, groupSearch: e.target.value})}
                  className='pl-10 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                />
              </div>
              <ScrollArea className='h-[200px] border rounded-lg p-3'>
                {groupsLoading ? (
                  <div className='space-y-2'>
                    <Skeleton className='h-10 w-full' />
                    <Skeleton className='h-10 w-full' />
                    <Skeleton className='h-10 w-full' />
                  </div>
                ) : groups.length > 0 ? (
                  <div className='space-y-2'>
                    {groups.map((group) => {
                      const isSelected = groupForm.selectedGroups.some(
                        (g) => g.vbaGroupId === group.vbaGroupId,
                      );
                      return (
                        <div
                          key={group.vbaGroupId}
                          className={`flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                            isSelected ? 'bg-brand-blue/5' : ''
                          }`}
                          onClick={() => handleGroupToggle(group)}
                        >
                          <Checkbox checked={isSelected} />
                          <div className='flex-1'>
                            <div className='font-medium text-brand-navy'>{group.groupName}</div>
                            <div className='text-xs text-brand-gray'>
                              Group ID: {group.vbaGroupId}
                            </div>
                          </div>
                          <Badge
                            className={
                              group.status === 'ACTIVE'
                                ? 'bg-emerald text-white border-0'
                                : 'bg-brand-gray text-white border-0'
                            }
                          >
                            {group.status}
                          </Badge>
                        </div>
                      );
                    })}
                    {hasNextPage && (
                      <div ref={loadMoreRef} className='flex justify-center py-2'>
                        {isFetchingNextPage && (
                          <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='text-center py-4 text-brand-gray'>
                    No groups found for this affiliation
                  </div>
                )}
              </ScrollArea>
              {groupForm.selectedGroups.length > 0 && (
                <div className='mt-2'>
                  <Badge className='bg-brand-blue/10 text-brand-blue border-0 font-medium'>
                    {groupForm.selectedGroups.length} group
                    {groupForm.selectedGroups.length > 1 ? 's' : ''} selected
                  </Badge>
                </div>
              )}
            </div>
          </>
        )}
        <AssignableRolePicker
          scope='GROUP'
          clientId={groupForm.clientId || undefined}
          idPrefix='group'
          value={groupForm.role}
          onChange={(role) => setGroupForm({...groupForm, role})}
        />
        <FeatureFlagEditor
          idPrefix='group'
          flags={groupForm.featureFlags}
          onChange={(featureFlags) => setGroupForm({...groupForm, featureFlags})}
        />
        <div className='space-y-2'>
          <Label htmlFor='group-expiry'>Code Expiry (days)</Label>
          <Select
            value={groupForm.expiryDays.toString()}
            onValueChange={(value) => setGroupForm({...groupForm, expiryDays: parseInt(value)})}
          >
            <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITE_EXPIRATION_DAY_OPTIONS.map((days) => (
                <SelectItem key={days} value={days.toString()}>
                  {days} day{days > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
          onClick={handleGroupSubmit}
          disabled={
            !groupForm.email ||
            !groupForm.name ||
            !groupForm.clientId ||
            !groupForm.role ||
            groupForm.selectedGroups.length === 0 ||
            createGroupUser.isPending
          }
        >
          {createGroupUser.isPending ? (
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
          ) : (
            <UserPlus className='h-4 w-4 mr-2' />
          )}
          Create Group User Registration
        </Button>
      </CardContent>
    </Card>
  );
}
